import createClient, { type Middleware } from "openapi-fetch";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { notifyForbiddenFromResponse } from "@/lib/api-error";
import type { components, paths } from "@dolfin/api-admin";

export type DolfinClient = ReturnType<typeof createClient<paths>>;
type AuthResponse = components["schemas"]["AuthResponse"];
type AuthOrganisation = components["schemas"]["AuthOrganisation"];

const DEFAULT_BASE_URL = "https://api.dolfinai.co";

// True when the JWT expires within a minute (or can't be read), so it's renewed before use rather than after a 401.
export function tokenIsDue(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp !== "number" || payload.exp * 1000 - 60_000 <= Date.now();
  } catch {
    return true;
  }
}

export interface DolfinProviderProps {
  /**
   * Exchanges a userId for a Dolfin AuthResponse, typically via a backend wrapping POST /auth/codes + /auth/exchange:
   * the API key must never reach the browser. A 401 triggers a refresh and, given a new token, one replay.
   */
  fetchToken: (args: { userId: string }) => Promise<AuthResponse>;
  /** Identifies the end user. Token is refetched whenever this changes. */
  userId?: string;
  /** Sent as `x-dolfin-organisation-id`; switching needs no token refetch, as the token covers every organisation. */
  organisationId?: string;
  /** API base URL. Defaults to `https://api.dolfinai.co`. */
  baseUrl?: string;
  children: ReactNode;
}

export interface DolfinAuthState {
  auth: AuthResponse | null;
  organisationId: string | undefined;
  /** Role for the currently-selected organisation, if known. Useful for permission-gating UI. */
  role: AuthOrganisation["role"] | null;
  loading: boolean;
  error: Error | null;
  /** Force a token refresh. Returns the new AuthResponse, or null if userId is unset. */
  refresh: () => Promise<AuthResponse | null>;
  baseUrl: string;
  /** For endpoints the typed client can't model (agent SSE streams). Same token, headers and 401 replay as the client. */
  authFetch: (path: string, init?: RequestInit) => Promise<Response>;
}

const DolfinClientContext = createContext<DolfinClient | null>(null);
const DolfinAuthContext = createContext<DolfinAuthState | null>(null);

export function DolfinProvider({
  fetchToken,
  userId,
  organisationId,
  baseUrl = DEFAULT_BASE_URL,
  children,
}: DolfinProviderProps) {
  const [auth, setAuth] = useState<AuthResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const tokenRef = useRef<string | null>(null);
  const orgIdRef = useRef<string | undefined>(organisationId);
  const userIdRef = useRef<string | undefined>(userId);
  const fetchTokenRef = useRef(fetchToken);
  const inflightRef = useRef<Promise<AuthResponse | null> | null>(null);

  tokenRef.current = auth?.token ?? null;
  orgIdRef.current = organisationId;
  userIdRef.current = userId;
  fetchTokenRef.current = fetchToken;

  const refresh = useMemo(
    () => async (): Promise<AuthResponse | null> => {
      const currentUserId = userIdRef.current;
      if (!currentUserId) return null;
      if (inflightRef.current) return inflightRef.current;

      const promise = (async () => {
        setLoading(true);
        try {
          const next = await fetchTokenRef.current({ userId: currentUserId });
          // Before setAuth, so middleware awaiting this promise sees the fresh token without a re-render.
          tokenRef.current = next.token ?? null;
          setAuth(next);
          setError(null);
          return next;
        } catch (e) {
          const err = e instanceof Error ? e : new Error(String(e));
          setError(err);
          throw err;
        } finally {
          setLoading(false);
          inflightRef.current = null;
        }
      })();

      inflightRef.current = promise;
      return promise.catch(() => null);
    },
    [],
  );

  useEffect(() => {
    if (!userId) {
      setAuth(null);
      setError(null);
      return;
    }
    refresh();
  }, [userId, refresh]);

  const client = useMemo(() => {
    const c = createClient<paths>({ baseUrl });
    // Unsent copies of in-flight requests, by request id, so a 401 can be replayed once with a fresh token.
    const replayable = new Map<string, Request>();
    const middleware: Middleware = {
      async onRequest({ request, id }) {
        // Children can fire requests before the mount effect's refresh(); fetch the token lazily and wait for it.
        if (userIdRef.current && (!tokenRef.current || tokenIsDue(tokenRef.current))) {
          if (!inflightRef.current) refresh();
          if (inflightRef.current) {
            try {
              await inflightRef.current;
            } catch {
              // Failed; let the request proceed and surface a 401 to the caller.
            }
          }
        }
        if (tokenRef.current) {
          request.headers.set("Authorization", `Bearer ${tokenRef.current}`);
        }
        if (orgIdRef.current) {
          request.headers.set("x-dolfin-organisation-id", orgIdRef.current);
        }
        replayable.set(id, request.clone());
        return request;
      },
      async onResponse({ request, response, id }) {
        const copy = replayable.get(id);
        replayable.delete(id);
        if (response.status === 401 && tokenRef.current) {
          const sent = request.headers.get("Authorization");
          const next = await refresh();
          if (copy && next?.token && `Bearer ${next.token}` !== sent) {
            copy.headers.set("Authorization", `Bearer ${next.token}`);
            const retried = await fetch(copy);
            void notifyForbiddenFromResponse(retried);
            return retried;
          }
        }
        // A module the client has not bought 403s every request on the page, so
        // the toast lives here (deduped by module) rather than at each call site.
        void notifyForbiddenFromResponse(response);
        return response;
      },
      onError({ id }) {
        replayable.delete(id);
      },
    };
    c.use(middleware);
    return c;
  }, [baseUrl, refresh]);

  const authFetch = useMemo(
    () =>
      async (path: string, init?: RequestInit): Promise<Response> => {
        // As in the client middleware: fetch a missing or due token before attaching headers.
        if (userIdRef.current && (!tokenRef.current || tokenIsDue(tokenRef.current))) {
          if (!inflightRef.current) refresh();
          if (inflightRef.current) {
            try {
              await inflightRef.current;
            } catch {
              // Failed; let the request proceed and surface a 401 to the caller.
            }
          }
        }

        const url = /^https?:\/\//.test(path) ? path : `${baseUrl}${path}`;
        const request = new Request(url, init);
        // Kept unsent so the body can go a second time if the first attempt 401s.
        const retry = request.clone();
        const sent = tokenRef.current;
        if (sent) request.headers.set("Authorization", `Bearer ${sent}`);
        if (orgIdRef.current) request.headers.set("x-dolfin-organisation-id", orgIdRef.current);

        let response = await fetch(request);
        if (response.status === 401 && sent) {
          const next = await refresh();
          if (next?.token && next.token !== sent) {
            retry.headers.set("Authorization", `Bearer ${next.token}`);
            if (orgIdRef.current) retry.headers.set("x-dolfin-organisation-id", orgIdRef.current);
            response = await fetch(retry);
          }
        }
        void notifyForbiddenFromResponse(response);
        return response;
      },
    [baseUrl, refresh],
  );

  const authState = useMemo<DolfinAuthState>(() => {
    const role =
      auth?.organisations?.find((o) => o.organisationId === organisationId)?.role ?? null;
    return { auth, organisationId, role, loading, error, refresh, baseUrl, authFetch };
  }, [auth, organisationId, loading, error, refresh, baseUrl, authFetch]);

  return (
    <DolfinClientContext.Provider value={client}>
      <DolfinAuthContext.Provider value={authState}>{children}</DolfinAuthContext.Provider>
    </DolfinClientContext.Provider>
  );
}

export function useDolfinClient(): DolfinClient {
  const ctx = useContext(DolfinClientContext);
  if (!ctx) throw new Error("useDolfinClient must be used inside <DolfinProvider>");
  return ctx;
}

export function useDolfinAuth(): DolfinAuthState {
  const ctx = useContext(DolfinAuthContext);
  if (!ctx) throw new Error("useDolfinAuth must be used inside <DolfinProvider>");
  return ctx;
}
